// A minimal history router.
//
// The app has about a dozen screens and no nested-layout needs, so a router library
// would be more configuration than code. This is the whole thing: a path in state,
// pushState to navigate, and popstate to go back.

import { useCallback, useEffect, useState } from 'react';

export function useRoute(): [string, (path: string, replace?: boolean) => void] {
  const [path, setPath] = useState(() => window.location.pathname + window.location.search);

  useEffect(() => {
    const onPop = (): void => setPath(window.location.pathname + window.location.search);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((next: string, replace = false): void => {
    if (replace) window.history.replaceState({}, '', next);
    else window.history.pushState({}, '', next);
    setPath(next);
    // Deep-linking into a screen should start at the top of it, not wherever the
    // previous screen happened to be scrolled to.
    window.scrollTo(0, 0);
  }, []);

  return [path, navigate];
}

export interface Match {
  screen: string;
  id?: string;
  query: URLSearchParams;
}

/** Maps a path to a screen. Unknown paths fall through to Today. */
export function matchRoute(path: string): Match {
  const [pathname, search = ''] = path.split('?');
  const query = new URLSearchParams(search);
  const segments = (pathname ?? '/').split('/').filter(Boolean);

  const [first, second, third] = segments;

  if (!first) return { screen: 'today', query };

  switch (first) {
    case 'experiments':
      if (!second) return { screen: 'experiments', query };
      if (second === 'new') return { screen: 'wizard', query };
      if (third === 'checkin') return { screen: 'checkin', id: second, query };
      if (third === 'report') return { screen: 'report', id: second, query };
      if (third === 'doctor') return { screen: 'doctor', id: second, query };
      return { screen: 'experiment', id: second, query };
    case 'timeline':
      return { screen: 'timeline', query };
    case 'medications':
      return { screen: 'medications', query };
    case 'scripts':
      return { screen: 'scripts', query };
    case 'profile':
      return { screen: 'profile', query };
    case 'settings':
      return { screen: 'settings', query };
    case 'search':
      return { screen: 'search', query };
    default:
      return { screen: 'today', query };
  }
}
